import { FC } from 'react'

import {
  BankingTransferFilter,
  TransfersAmountWidgetDocument,
  TransfersAmountWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency?: string) => string
}

interface QueryArgsType {}

const prepareData = (
  data: TransfersAmountWidgetQuery,
  prepareDataArgs: PreparedDataArgsType,
  amountCurrency?: string[]
) => {
  const [firstCurrency, ...others] = amountCurrency || []
  const [firstCurrencyFromAggregates, ...othersFromAggregates] =
    data?.banking?.transfersConnection?.aggregate?.terms?.currency?.values || []

  if (!!others.length || !!othersFromAggregates.length) {
    return {
      countersChartDatas: [
        {
          label: i18n.t('finance.overview.outstanding_payments.total'),
          value: 0,
          formattedValue: '--',
        },
      ],
    }
  }

  const amount = data?.banking?.transfersConnection?.aggregate?.sum?.amountCents || 0

  return {
    countersChartDatas: [
      {
        label: i18n.t('finance.overview.outstanding_payments.total'),
        value: amount,
        formattedValue: prepareDataArgs.amountFormatter(
          amount,
          firstCurrency || firstCurrencyFromAggregates
        ),
      },
    ],
  }
}

export const buildTransfersAmountWidget = ({
  widgetTitle,
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  widgetTitle?: string
  queryArgs?: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: BankingTransferFilter
}) => {
  const TransfersAmountWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas } = data

    return (
      <FinanceWidget
        id="transfersAmountDashboard"
        type={SupportedCharts.COUNTERS}
        showChart={false}
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
          label: widgetTitle || chartData.label,
        }))}
      />
    )
  }

  // This is to avoid problems with date filter and between provoked by the use of datatable filters
  const filteredTransferFilters = removeNullBetween(filters)
  const filterCurrencies = filters.currency?.in?.flatMap((currency) => currency)

  return buildWidget({
    id: 'transfersAmountWidget',
    component: TransfersAmountWidget,
    document: TransfersAmountWidgetDocument,
    prepareData: (data: TransfersAmountWidgetQuery) =>
      prepareData(data, prepareDataArgs, filterCurrencies),
    queryArgs: {
      ...queryArgs,
      transfersFilters: { ...filteredTransferFilters },
    },
  })
}
